import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import useQuery from 'hooks/useQuery'
import {
  filterEmptyValues,
  getDepartmentOptions,
  getFamiliesOptionsBySection,
  getSectionOptionsByDepartment,
} from 'utils/helpers'
import { Button, Dropdown, DurationInput, TextInput } from 'components/common'
import { MASTER_DATA_FORM_FIELDS } from 'utils/constants'
import AggregatorsDropdown from './aggregatorsDropdown'

const {
  ITEM_NUMBER,
  ACTIVE_ONLINE,
  BARCODE,
  DEPARTMENT,
  ERP_BRAND,
  ERP_ITEM_DESC,
  ERP_MARKETING_CODE,
  ERP_PACK_INFO,
  ERP_UOM,
  FAMILY,
  HIDE_AGGREGATORS,
  ONLINE_DESC,
  ONLINE_PACK_INFO,
  QTY_PER_UOM,
  SECTION,
  SUB_FAMILY,
  VAT_CLASS,
  VAT_PERCENTAGE,
} = MASTER_DATA_FORM_FIELDS

type Props = {
  toggleFilterForm: () => void
}

export const FILTER_FIELD_MASTER_DATA = 'masterDataFilters'

const MasterDataFilterForm = ({ toggleFilterForm }: Props) => {
  const { getQuery, removeQuery, setMultipleQueries } = useQuery()
  const filters = getQuery(FILTER_FIELD_MASTER_DATA)

  const [resetFilter, setResetFilter] = useState(false)

  const { register, handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: filters,
  })

  const onFilterApply = (data: any) => {
    if (data?.dept === 'NONE') {
      delete data.dept
    }

    if (data?.section === 'NONE') {
      delete data.section
    }

    if (data?.family === 'NONE') {
      delete data.family
    }

    if (data?.activeOnline === 'NONE') {
      delete data.activeOnline
    }

    const filteredData = filterEmptyValues(data)

    if (Object.values(filteredData).length > 0) {
      setMultipleQueries({ [FILTER_FIELD_MASTER_DATA]: filteredData, page: 1 })
    } else {
      removeQuery(FILTER_FIELD_MASTER_DATA)
    }

    toggleFilterForm()
  }

  // will watch the change of Department and Section Fields
  const deptWatch = watch('dept')
  const sectionWatch = watch('section')

  // Department Options
  const deptOptions = useMemo(() => getDepartmentOptions(), [])

  //  Section Options
  const sectionOptions = useMemo(() => {
    const dept = deptWatch || deptOptions[0]?.value

    return getSectionOptionsByDepartment(dept)
  }, [deptWatch, getSectionOptionsByDepartment])

  //  Family Options
  const familyOptions = useMemo(() => {
    const section = sectionWatch || sectionOptions[0]?.value

    return getFamiliesOptionsBySection(section)
  }, [sectionWatch, deptWatch, sectionOptions])

  const cancelFilter = () => {
    removeQuery('masterDataFilters')
    reset()
    setResetFilter((pre) => !pre)
    toggleFilterForm()
  }

  return (
    <div className="mt-2 rounded-lg border bg-white px-5 py-3 ">
      <div className="mx-auto max-w-5xl">
        <form onSubmit={handleSubmit(onFilterApply)}>
          <div className="grid grid-cols-2 gap-x-14 gap-y-3 md:grid-cols-3">
            {/* Item Number */}
            <DurationInput
              register={register}
              startText="From"
              endText="To"
              title={ITEM_NUMBER.label}
            />

            {/* Barcode */}
            <TextInput
              register={register}
              name={BARCODE.key}
              label={BARCODE.label}
            />

            {/* Department */}
            <Dropdown
              name={DEPARTMENT.key}
              label={DEPARTMENT.label}
              options={deptOptions}
              setValue={setValue}
              defaultValue={filters?.dept || resetFilter}
            />

            {/* Section */}
            <Dropdown
              name={SECTION.key}
              label={SECTION.label}
              options={sectionOptions}
              setValue={setValue}
              defaultValue={filters?.section || sectionOptions?.[1]}
            />

            {/* Family */}
            <Dropdown
              name={FAMILY.key}
              label={FAMILY.label}
              options={familyOptions}
              setValue={setValue}
              defaultValue={filters?.family || familyOptions?.[1]}
            />

            {/* Sub Family */}
            <TextInput
              name={SUB_FAMILY.key}
              label={SUB_FAMILY.label}
              register={register}
            />

            {/* ERP Item Description */}
            <TextInput
              name={ERP_ITEM_DESC.key}
              label={ERP_ITEM_DESC.label}
              register={register}
            />

            {/* ERP UOM */}
            <TextInput
              name={ERP_UOM.key}
              label={ERP_UOM.label}
              register={register}
            />

            {/* QTY ERP UOM */}
            <TextInput
              register={register}
              name={QTY_PER_UOM.key}
              label={QTY_PER_UOM.label}
            />

            {/* ERP Pack Info */}
            <TextInput
              register={register}
              name={ERP_PACK_INFO.key}
              label={ERP_PACK_INFO.label}
            />

            {/* ERP Brand */}
            <TextInput
              register={register}
              name={ERP_BRAND.key}
              label={ERP_BRAND.label}
            />

            {/* ERP Marketing Code */}
            <TextInput
              register={register}
              name={ERP_MARKETING_CODE.key}
              label={ERP_MARKETING_CODE.label}
            />

            {/* Hide Aggregators */}
            <AggregatorsDropdown
              defaultSelected={filters?.[HIDE_AGGREGATORS.key]}
              resetForm={resetFilter}
              setValue={setValue}
              name={HIDE_AGGREGATORS.key}
              label={HIDE_AGGREGATORS.label}
              isActive
            />

            {/* Online Description */}
            <TextInput
              register={register}
              name={ONLINE_DESC.key}
              label={ONLINE_DESC.label}
            />

            {/* Online Pack Info */}
            <TextInput
              register={register}
              name={ONLINE_PACK_INFO.key}
              label={ONLINE_PACK_INFO.label}
            />

            {/* Active Online */}
            <Dropdown
              name={ACTIVE_ONLINE.key}
              label={ACTIVE_ONLINE.label}
              setValue={setValue}
              defaultValue={filters?.[ACTIVE_ONLINE.key] || resetFilter}
              options={[
                { label: '--- NONE ---', value: 'NONE', disabled: false },
                { label: 'Yes', value: 'true', disabled: false },
                {
                  label: 'No',
                  value: 'false',
                  disabled: false,
                },
              ]}
            />

            {/* Vat Class */}
            <TextInput
              register={register}
              name={VAT_CLASS.key}
              label={VAT_CLASS.label}
            />

            {/* Vat Percentage */}
            <TextInput
              register={register}
              name={VAT_PERCENTAGE.key}
              label={VAT_PERCENTAGE.label}
            />
          </div>
          <div className="my-4 flex gap-5">
            <Button buttonText="Apply Filters" className="w-32" />

            <Button
              buttonText="cancel"
              variant="outline"
              type="button"
              onClick={cancelFilter}
              className="w-32"
            />
          </div>
        </form>
      </div>
    </div>
  )
}

export default MasterDataFilterForm
